import { createContext, ReactNode, useCallback, useContext, useState } from "react";
import { createStore, useStore } from "zustand";
import { combine } from "zustand/middleware";

import { FilterQuery, RoutesFacets } from "./useFilteredRoutes";

const createRoutesTableStore = () =>
  createStore(
    combine(
      {
        selectedRouteUuids: new Set<string>(),
        highlightedRouteUuids: new Set<string>(),
        filterParams: {} as FilterQuery,
        displayedColumns: undefined as Set<string> | undefined,
        sortOrder: [] as string[],
      },
      (set) => ({
        setSelectedRouteUuids: (selectedRouteUuids: string[]) =>
          set({ selectedRouteUuids: new Set(selectedRouteUuids) }),
        setHighlightedRouteUuids: (highlightedRouteUuids: string[]) =>
          set({ highlightedRouteUuids: new Set(highlightedRouteUuids) }),
        addToSelection: (uuids: string[]) =>
          set((state) => ({
            selectedRouteUuids: new Set([...state.selectedRouteUuids, ...uuids]),
          })),
        removeFromSelection: (uuids: string[]) =>
          set((state) => ({
            selectedRouteUuids: new Set(
              [...state.selectedRouteUuids].filter((uuid) => !uuids.includes(uuid)),
            ),
          })),
        setFilterParam: <K extends keyof FilterQuery>(key: K, value: FilterQuery[K]) => {
          set((state) => ({
            filterParams: {
              ...state.filterParams,
              [key]: value,
            },
          }));
        },
        setFilterParams: (params: FilterQuery) => {
          set(() => ({
            filterParams: params,
          }));
        },
        removeFilterParam: <K extends keyof FilterQuery>(key: K) => {
          set((state) => {
            const { [key]: _, ...rest } = state.filterParams;
            return { filterParams: rest };
          });
        },
        clearFilterParams: () => {
          set(() => ({
            filterParams: {},
          }));
        },
      }),
    ),
  );

type RoutesTableStore = ReturnType<typeof createRoutesTableStore>;

// Stands in for a real store when there is no provider, so that useStore is
// still called and the hook count stays the same either way. Nothing reads its
// state: callers that opted in to `optional` get undefined back instead.
const NO_STORE = {
  getState: () => undefined,
  getInitialState: () => undefined,
  subscribe: () => () => {},
} as unknown as RoutesTableStore;

const RoutesTableStoreContext = createContext<RoutesTableStore>(NO_STORE);

export function RoutesTableStoreProvider({ children }: { children: ReactNode }) {
  const [store] = useState(createRoutesTableStore);

  return (
    <RoutesTableStoreContext.Provider value={store}>{children}</RoutesTableStoreContext.Provider>
  );
}

type RoutesTableStoreState = ReturnType<ReturnType<typeof createRoutesTableStore>["getState"]>;

type RoutesTableStoreSelector = (state: RoutesTableStoreState) => unknown;

const identitySelector: RoutesTableStoreSelector = (state) => state;
const noStoreSelector: RoutesTableStoreSelector = () => undefined;

export function useRoutesTableStore(): RoutesTableStoreState;
export function useRoutesTableStore<T>(selector: (state: RoutesTableStoreState) => T): T;
export function useRoutesTableStore<T>(
  selector: (state: RoutesTableStoreState) => T,
  options: { optional: true },
): T | undefined;
export function useRoutesTableStore(
  selector: undefined,
  options: { optional: true },
): RoutesTableStoreState | undefined;
export function useRoutesTableStore<T>(
  selector?: (state: RoutesTableStoreState) => T,
  options?: { optional: true },
): T | RoutesTableStoreState | undefined {
  const store = useContext(RoutesTableStoreContext);
  const missing = store === NO_STORE;

  if (missing && !options?.optional) {
    throw new Error("useRoutesTableStore must be used within a RoutesTableStoreProvider");
  }

  const resolvedSelector: RoutesTableStoreSelector = missing
    ? noStoreSelector
    : (selector ?? identitySelector);

  return useStore(store, resolvedSelector) as T | RoutesTableStoreState | undefined;
}

const RouteFacetsProviderContext = createContext<RoutesFacets | undefined>(undefined);

export function RouteFacetsProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: RoutesFacets | undefined;
}) {
  return (
    <RouteFacetsProviderContext.Provider value={value}>
      {children}
    </RouteFacetsProviderContext.Provider>
  );
}

export function useRoutesFacets() {
  const facets = useContext(RouteFacetsProviderContext);

  return facets;
}

export function useSetFilterParam() {
  return useRoutesTableStore(
    useCallback((state: RoutesTableStoreState) => state.setFilterParam, []),
  );
}

export function useFilterParams() {
  return useRoutesTableStore(useCallback((state: RoutesTableStoreState) => state.filterParams, []));
}
